import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_TITLE,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
  PRIMARY_LIGHT,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  SHADOW_LG,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';
import { DISTRIBUTOR_TAG_FILTERS } from 'components/upb_viewer_beta/distributorFilters';
import useGet from 'lib/hooks/useGet';
import { GLOBAL_VENDORS_FOR_DROPDOWN } from 'lib/networking/endpoints';

const ALL_DISTRIBUTORS = 'All Distributors';

/**
 * BetaDistributorFilter — distributor (vendor) picker shown as the first item in
 * the KPI strip. Selecting a distributor filters both the KPI metrics and funnel.
 *
 * Below "All Distributors" it also offers the Influence-campaign distributor
 * groups (DISTRIBUTOR_TAG_FILTERS); picking one scopes the viewer's TAM to every
 * distributor partner carrying that `third_party` tag.
 *
 * The selection is mutually exclusive: a single vendor id OR a tag group.
 *
 * Props:
 *   value            number | null  — selected vendor id (null = all / tag group)
 *   thirdPartyTag    string | null  — selected distributor-group tag (null = none)
 *   onChange         ({ vendorId: number | null, thirdPartyTag: string | null }) => void
 *   showGroupFilters bool           — show the Influence-campaign DP-group options
 *                                     (off for per-product contexts where a group
 *                                     scope is meaningless, e.g. segment analysis)
 */
function BetaDistributorFilter({ value, thirdPartyTag, onChange, compact, showGroupFilters }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);

  const { data: { data: vendors = [] } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activeTagFilter = DISTRIBUTOR_TAG_FILTERS.find(f => f.tag === thirdPartyTag);
  let selectedName = ALL_DISTRIBUTORS;
  if (activeTagFilter) {
    selectedName = activeTagFilter.label;
  } else if (value != null) {
    selectedName = vendors.find(v => v.id === value)?.name || ALL_DISTRIBUTORS;
  }

  const lc = search.trim().toLowerCase();
  const filtered = vendors.filter(v => !lc || v.name.toLowerCase().includes(lc));

  const closeMenu = () => {
    setOpen(false);
    setSearch('');
  };

  const selectVendor = id => {
    onChange({ vendorId: id, thirdPartyTag: null });
    closeMenu();
  };

  const selectTag = tag => {
    onChange({ vendorId: null, thirdPartyTag: tag });
    closeMenu();
  };

  return (
    <div
      ref={rootRef}
      className={css(styles.filter, compact && styles.filterCompact, open && styles.filterOpen)}
      onClick={() => setOpen(o => !o)}
    >
      <div className={css(styles.overline)}>Distributor</div>
      <div className={css(styles.row)}>
        <div className={css(styles.name, compact && styles.nameCompact)}>{selectedName}</div>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={css(styles.chevron, open && styles.chevronOpen)}
        />
      </div>

      {open && (
        <div className={css(styles.menu)} onClick={e => e.stopPropagation()}>
          <div className={css(styles.menuSearch)}>
            <FontAwesomeIcon icon={faSearch} className={css(styles.searchIcon)} />
            <input
              className={css(styles.searchInput)}
              placeholder="Find distributor…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className={css(styles.menuList)}>
            <div
              className={css(styles.opt, value == null && !thirdPartyTag && styles.optActive)}
              onClick={() => selectVendor(null)}
            >
              <span>{ALL_DISTRIBUTORS}</span>
            </div>
            {/* Influence-campaign distributor groups, pinned above the
                searchable per-distributor list. */}
            {showGroupFilters &&
              DISTRIBUTOR_TAG_FILTERS.map(f => (
                <div
                  key={f.tag}
                  className={css(styles.opt, thirdPartyTag === f.tag && styles.optActive)}
                  onClick={() => selectTag(f.tag)}
                >
                  <span>{f.label}</span>
                </div>
              ))}
            {filtered.map(v => (
              <div
                key={v.id}
                className={css(styles.opt, value === v.id && styles.optActive)}
                onClick={() => selectVendor(v.id)}
              >
                <span>{v.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  filter: {
    position: 'relative',
    minWidth: 230,
    backgroundColor: BG_2,
    border: `1px solid ${BORDER_2}`,
    borderRadius: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 4,
    ':hover': {
      backgroundColor: WHITE,
    },
  },

  filterOpen: {
    backgroundColor: WHITE,
    borderColor: PRIMARY_COLOR,
    boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA}`,
  },

  // Inline one-line variant (Segment Analysis filter row): label + value sit on
  // a single row with tighter padding — far less bulky than the stacked KPI box.
  filterCompact: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: '7px 12px',
    minWidth: 0,
  },

  overline: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: 10,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },

  name: {
    fontFamily: FONT_TITLE,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: 16,
    color: FG_1,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 180,
  },

  nameCompact: {
    fontSize: 14,
  },

  chevron: {
    fontSize: 12,
    color: FG_3,
    opacity: 0.55,
    flexShrink: 0,
    transition: 'transform 0.15s',
  },

  chevronOpen: {
    transform: 'rotate(180deg)',
  },

  menu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    width: 300,
    zIndex: 30,
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_LG,
    overflow: 'hidden',
    cursor: 'default',
  },

  menuSearch: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderBottom: `1px solid ${BORDER_2}`,
    backgroundColor: BG_2,
  },

  searchIcon: {
    fontSize: 14,
    color: FG_3,
    opacity: 0.55,
    flexShrink: 0,
  },

  searchInput: {
    border: 'none',
    background: 'transparent',
    outline: 'none',
    flex: 1,
    fontFamily: FONT_BODY,
    fontWeight: 400,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    minWidth: 0,
  },

  menuList: {
    maxHeight: 280,
    overflowY: 'auto',
    padding: '4px 0',
  },

  opt: {
    padding: '9px 14px',
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    ':hover': {
      backgroundColor: BG_2,
    },
  },

  optActive: {
    backgroundColor: PRIMARY_LIGHT,
    color: PRIMARY_HOVER,
    fontWeight: FONT_WEIGHT_BOLD,
  },
});

BetaDistributorFilter.propTypes = {
  value: PropTypes.number,
  thirdPartyTag: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  showGroupFilters: PropTypes.bool,
};

BetaDistributorFilter.defaultProps = {
  value: null,
  thirdPartyTag: null,
  compact: false,
  showGroupFilters: true,
};

export default BetaDistributorFilter;
